/**
 * Push Notification Service
 * Handles Web Push notification subscription and management
 * CRITICAL: This service enables real-time push notifications for lead assignments
 */

import apiClient from './api';

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// Detect device type
const getDeviceType = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for mobile devices
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'ios';
  }
  return 'web';
};

// Get device name
const getDeviceName = () => {
  const userAgent = navigator.userAgent;
  
  // Try to get a friendly device name
  if (/Chrome/i.test(userAgent) && /Mobile/i.test(userAgent)) {
    return 'Chrome Mobile';
  }
  if (/Chrome/i.test(userAgent)) {
    return 'Chrome Desktop';
  }
  if (/Safari/i.test(userAgent) && /Mobile/i.test(userAgent)) {
    return 'Safari Mobile';
  }
  if (/Safari/i.test(userAgent)) {
    return 'Safari Desktop';
  }
  if (/Firefox/i.test(userAgent)) {
    return 'Firefox';
  }
  if (/Edge/i.test(userAgent)) {
    return 'Edge';
  }
  return 'Unknown Browser';
};

class NotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.vapidPublicKey = null;
    this.permissionRequested = false;
  }

  /**
   * Check if notifications are supported
   */
  isSupported() {
    return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Get current notification permission status
   */
  getPermissionStatus() {
    if (!this.isSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Check if permission was already requested this session
   */
  wasPermissionRequested() {
    return this.permissionRequested || localStorage.getItem('notification_permission_requested') === 'true';
  }

  /**
   * Mark permission as requested
   */
  markPermissionRequested() {
    this.permissionRequested = true;
    localStorage.setItem('notification_permission_requested', 'true');
  }

  /**
   * Register service worker
   */
  async registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers are not supported');
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });
      
      console.log('[NotificationService] Service Worker registered:', registration);
      this.registration = registration;
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      
      return registration;
    } catch (error) {
      console.error('[NotificationService] Service Worker registration failed:', error);
      throw error;
    }
  }

  /**
   * Request notification permission from user
   * Returns: true if granted, false if denied, null if dismissed
   */
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Notifications are not supported');
    }

    // Already granted
    if (Notification.permission === 'granted') {
      return true;
    }

    // Already denied - user must manually enable in browser settings
    if (Notification.permission === 'denied') {
      return false;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    this.markPermissionRequested();
    
    return permission === 'granted';
  }

  /**
   * Get VAPID public key from backend
   */
  async getVapidPublicKey() {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const response = await apiClient.get('/notifications/vapid-public-key');
      this.vapidPublicKey = response.data.publicKey;
      return this.vapidPublicKey;
    } catch (error) {
      console.error('[NotificationService] Error getting VAPID public key:', error);
      throw error;
    }
  }

  /**
   * Subscribe to push notifications
   * This is the critical function for enabling lead assignment notifications
   */
  async subscribe() {
    try {
      // Check if already subscribed
      if (this.subscription) {
        console.log('[NotificationService] Already subscribed to push notifications');
        return this.subscription;
      }

      // Request permission
      const permissionGranted = await this.requestPermission();
      if (!permissionGranted) {
        console.warn('[NotificationService] Notification permission denied');
        throw new Error('Notification permission denied. Please enable notifications in browser settings to receive lead alerts.');
      }

      // Register service worker
      if (!this.registration) {
        await this.registerServiceWorker();
      }

      // Get VAPID public key
      const vapidPublicKey = await this.getVapidPublicKey();
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

      // Subscribe to push notifications
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      console.log('[NotificationService] Push notification subscription created');
      this.subscription = subscription;

      // Send subscription to backend with device info
      await this.sendSubscriptionToBackend(subscription);

      return subscription;
    } catch (error) {
      console.error('[NotificationService] Error subscribing to push notifications:', error);
      throw error;
    }
  }

  /**
   * Send subscription to backend with device tracking
   */
  async sendSubscriptionToBackend(subscription) {
    try {
      const deviceType = getDeviceType();
      const deviceName = getDeviceName();
      
      const response = await apiClient.post('/notifications/subscribe', {
        subscription: subscription.toJSON(),
        device_type: deviceType,
        device_name: deviceName
      });
      
      console.log('[NotificationService] Subscription sent to backend:', response.data);
      console.log(`[NotificationService] Device: ${deviceType} - ${deviceName}`);
      return response.data;
    } catch (error) {
      console.error('[NotificationService] Error sending subscription to backend:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    try {
      if (!this.subscription) {
        // Try to get existing subscription
        if (this.registration && this.registration.pushManager) {
          this.subscription = await this.registration.pushManager.getSubscription();
        }
      }

      if (this.subscription) {
        // Unsubscribe from push manager
        await this.subscription.unsubscribe();

        // Notify backend
        await apiClient.post('/notifications/unsubscribe', {
          endpoint: this.subscription.endpoint
        });

        console.log('[NotificationService] Unsubscribed from push notifications');
        this.subscription = null;
        return true;
      }

      return false;
    } catch (error) {
      console.error('[NotificationService] Error unsubscribing from push notifications:', error);
      throw error;
    }
  }

  /**
   * Check if user is subscribed
   */
  async isSubscribed() {
    try {
      if (!this.registration) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          this.registration = registration;
        } else {
          return false;
        }
      }

      const subscription = await this.registration.pushManager.getSubscription();
      this.subscription = subscription;
      return subscription !== null;
    } catch (error) {
      console.error('[NotificationService] Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Send a test notification
   */
  async sendTestNotification() {
    try {
      const response = await apiClient.post('/notifications/test', {
        title: 'Test Notification',
        body: 'This is a test notification from Patrick\'s Edu Care CRM. If you see this, notifications are working!'
      });
      console.log('[NotificationService] Test notification sent:', response.data);
      return response.data;
    } catch (error) {
      console.error('[NotificationService] Error sending test notification:', error);
      throw error;
    }
  }

  /**
   * Initialize notification service
   * This should be called on app startup
   */
  async initialize() {
    try {
      if (!this.isSupported()) {
        console.warn('[NotificationService] Push notifications are not supported');
        return false;
      }

      // Register service worker first
      await this.registerServiceWorker();

      // Check if already subscribed
      const subscribed = await this.isSubscribed();
      
      if (!subscribed && Notification.permission === 'granted') {
        // Permission granted but not subscribed, subscribe now
        await this.subscribe();
        return true;
      }

      return subscribed;
    } catch (error) {
      console.error('[NotificationService] Error initializing notification service:', error);
      return false;
    }
  }

  /**
   * Get subscription status details
   */
  async getStatus() {
    const isSupported = this.isSupported();
    const permission = this.getPermissionStatus();
    const isSubscribed = await this.isSubscribed();
    
    return {
      isSupported,
      permission,
      isSubscribed,
      deviceType: getDeviceType(),
      deviceName: getDeviceName(),
      canSubscribe: isSupported && permission !== 'denied'
    };
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
